import React from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';

// Capture d'une photo avant/après prestation (2026-09-05, Emeni) — `capture="environment"` ouvre
// directement l'appareil photo arrière sur mobile plutôt que la galerie. Redimensionnée côté client avant
// stockage (D1 n'a pas de stockage objet dans ce projet, tout passe en base64 dans la colonne) : une photo
// de constat n'a pas besoin d'être en pleine résolution, seulement lisible.
// `allowLibrary` (2026-09-22, Emeni) : ajoute un second bouton sans `capture` pour choisir une image déjà
// présente dans la galerie ou les fichiers (ex. Nos réalisations, où les photos sont souvent prises avant).
export function PhotoCaptureField({ label, value, onChange, maxSize = 1280, quality = 0.82, allowLibrary = false }: { label: string; value: string | null; onChange: (dataUrl: string | null) => void; maxSize?: number; quality?: number; allowLibrary?: boolean; }) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const libraryInputRef = React.useRef<HTMLInputElement>(null);

    const handleFile = (file: File | undefined) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const rawDataUrl = ev.target?.result as string;
            if (!rawDataUrl) return;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;
                if (width > maxSize || height > maxSize) {
                    if (width > height) { height = Math.round((height * maxSize) / width); width = maxSize; }
                    else { width = Math.round((width * maxSize) / height); height = maxSize; }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, width, height);
                onChange(canvas.toDataURL('image/jpeg', quality));
            };
            img.src = rawDataUrl;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">{label}</label>
            {value ? (
                <div className="relative rounded-xl overflow-hidden border border-border">
                    <img src={value} alt={label} className="w-full h-32 object-cover" />
                    <button type="button" onClick={() => onChange(null)}
                        className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-black/90">
                        <X size={12} />
                    </button>
                </div>
            ) : allowLibrary ? (
                <div className="h-32 grid grid-rows-2 gap-1.5">
                    <button type="button" onClick={() => inputRef.current?.click()}
                        className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border text-content-muted hover:text-content hover:border-brand-500 transition-all">
                        <Camera size={16} />
                        <span className="text-xs font-bold">Prendre une photo</span>
                    </button>
                    <button type="button" onClick={() => libraryInputRef.current?.click()}
                        className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border text-content-muted hover:text-content hover:border-brand-500 transition-all">
                        <ImagePlus size={16} />
                        <span className="text-xs font-bold">Choisir une image</span>
                    </button>
                </div>
            ) : (
                <button type="button" onClick={() => inputRef.current?.click()}
                    className="w-full h-32 flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border text-content-muted hover:text-content hover:border-brand-500 transition-all">
                    <Camera size={22} />
                    <span className="text-xs font-bold">Prendre une photo</span>
                </button>
            )}
            <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
            {allowLibrary && (
                <input ref={libraryInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
            )}
        </div>
    );
}
